/**
 * Data layer for the Pro home dashboard. Every panel's fetch lives here so the
 * page components stay presentational. All queries are read-mostly with short
 * stale times; the backend caches the dashboard widgets for 120s.
 */
import { useQuery } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';
import api from '../../../lib/api';
import type { CalendarEvent } from '../../../types/calendar';
import { SYSTEM_MILESTONE_TYPES } from '../../../constants/elections';

export interface NextCalendarEvent {
  calendar_id: number;
  reading_date: string;
  reading_time: string | null;
  reading_title: string;
  room_number: string | null;
  derived_location: string | null;
  chamber: string;
  upon_adjournment: boolean;
}

export interface HotBill {
  id: number;
  billNumber: string;
  title: string;
  status: string;
  lastUpdated: string;
  workspaceName: string;
  workspaceNames: string[];
  workspaceIds: string[];
  detailUrl: string;
  nextCalendarEvent: NextCalendarEvent | null;
}

export interface BillsStatusPayload {
  hotBills: HotBill[];
  totalTracked: number;
  totalTrackedAll?: number;
  trackedWorkspaceCount?: number;
}

export interface RecentChange {
  billId: number;
  billNumber: string;
  title: string;
  action: string;
  eventDate: string;
  createdAt: string;
  detailUrl: string;
}

export interface NewsItem {
  id: number;
  title: string;
  description: string | null;
  link: string;
  publishedDate: string | null;
  feedName: string;
  timeAgo: string;
}

export interface HappeningNowData {
  houseFloor: { title?: string; status?: string; audioUrl?: string } | null;
  senateFloor: { title?: string; status?: string; audioUrl?: string } | null;
  houseCommittees: Array<{ name?: string; title?: string }>;
  senateCommittees: Array<{ name?: string; title?: string }>;
  liveVideoUrl?: string;
  watchListenUrl?: string;
}

export interface ElectionInfo {
  calendarId: string;
  electionYear: number;
  name: string | null;
  generalDate: string | null;
  primaryDate: string | null;
}

export interface TrackedMeasure {
  id: string;
  ballot_title: string;
  /** Short public name; the home panel shows it in place of the SOS title. */
  popular_name: string | null;
  /** SOS unofficial caption, the short name when popular_name is unset. */
  unofficial_caption: string | null;
  measure_number: string | null;
  measure_type: string | null;
  status: string | null;
  election_year: number | null;
  workspaceNames: string[];
  workspaceIds: string[];
}

export const PRO_HOME_CALENDAR_DAYS = 14;

/**
 * Events across every workspace the user can reach, 14 days from startDate
 * (default today). The key includes the start so the panel can navigate to a
 * different window while the Today band keeps today's — same-window callers
 * share one fetch.
 */
export function useMyCalendarEvents(startDate?: string, sources?: string[], myEvents: boolean = true) {
  const start = startDate ?? format(new Date(), 'yyyy-MM-dd');
  const sourcesKey = sources && sources.length > 0 ? sources.join(',') : undefined;
  return useQuery(
    ['proHome', 'calendar', start, sourcesKey ?? 'legislative', myEvents ? 'my' : 'all'],
    async (): Promise<{ events: CalendarEvent[]; total: number }> => {
      const end = format(addDays(parseISO(start), PRO_HOME_CALENDAR_DAYS), 'yyyy-MM-dd');
      const response = await api.get('/calendar/events', {
        params: { start_date: start, end_date: end, my_events: myEvents || undefined, sources: sourcesKey, limit: 200 },
      });
      const data = response.data?.data;
      return { events: data?.events ?? [], total: data?.total ?? 0 };
    },
    // Refetch on every visit: the app cache is persisted and never refetches
    // on focus, so a 2-minute staleTime alone kept serving a list that missed
    // events teammates had just created. Own writes also invalidate this key
    // (WorkspaceEventModal, OrgEventsTab, EventWorkspaceSelectionModal).
    { staleTime: 2 * 60 * 1000, refetchOnMount: 'always' },
  );
}

export function useHotBills() {
  return useQuery(
    ['proHome', 'hotBills'],
    async (): Promise<BillsStatusPayload> => {
      const response = await api.get('/dashboard/widgets/bills_status/data');
      const data = response.data?.data?.data;
      return {
        hotBills: data?.hotBills ?? [],
        totalTracked: data?.totalTracked ?? 0,
        totalTrackedAll: data?.totalTrackedAll,
        trackedWorkspaceCount: data?.trackedWorkspaceCount,
      };
    },
    { staleTime: 2 * 60 * 1000 },
  );
}

export function useRecentChanges() {
  return useQuery(
    ['proHome', 'recentChanges'],
    async (): Promise<{ changes: RecentChange[]; total: number }> => {
      const response = await api.get('/dashboard/widgets/recent_changes/data');
      const data = response.data?.data?.data;
      return { changes: data?.changes ?? [], total: data?.total ?? 0 };
    },
    { staleTime: 2 * 60 * 1000 },
  );
}

export function useNewsFeed() {
  return useQuery(
    ['proHome', 'news'],
    async (): Promise<NewsItem[]> => {
      const response = await api.get('/dashboard/widgets/rss_news_feed/data');
      return response.data?.data?.data?.items ?? [];
    },
    { staleTime: 5 * 60 * 1000 },
  );
}

export function useHappeningNow() {
  return useQuery(
    ['proHome', 'happeningNow'],
    async (): Promise<HappeningNowData | null> => {
      const response = await api.get('/happening-now');
      return response.data ?? null;
    },
    { staleTime: 2 * 60 * 1000, retry: 1 },
  );
}

/**
 * Current election cycle plus its general/primary milestone dates. Two calls
 * because /current returns the cycle row without milestones.
 */
export function useElectionInfo() {
  return useQuery(
    ['proHome', 'electionInfo'],
    async (): Promise<ElectionInfo | null> => {
      const currentRes = await api.get('/election-calendars/current?state=CO');
      const current = currentRes.data?.data;
      if (!current?.id) return null;
      let milestones: Array<{ milestone_type: string; milestone_date: string }> = [];
      try {
        const detailRes = await api.get(`/election-calendars/${current.id}`);
        milestones = detailRes.data?.data?.milestones ?? [];
      } catch {
        // Milestones are optional — the countdown just won't render.
      }
      const findDate = (type: string) =>
        milestones.find((m) => m.milestone_type === type)?.milestone_date ?? null;
      return {
        calendarId: current.id,
        electionYear: Number(current.election_year),
        name: current.name || null,
        generalDate: findDate(SYSTEM_MILESTONE_TYPES.GENERAL),
        primaryDate: findDate(SYSTEM_MILESTONE_TYPES.PRIMARY),
      };
    },
    { staleTime: 60 * 60 * 1000 },
  );
}

/** Tracked ballot measures across the user's workspaces, with measure fields. */
export function useTrackedMeasures(enabled: boolean) {
  return useQuery(
    ['proHome', 'trackedMeasures'],
    async (): Promise<TrackedMeasure[]> => {
      const trackingRes = await api.get('/ballot-initiatives/tracking/mine');
      const tracking: Array<{
        ballot_initiative_id: string;
        workspace_id: string;
        workspace_name: string;
      }> = trackingRes.data?.data ?? [];
      if (tracking.length === 0) return [];

      const listRes = await api.get('/ballot-initiatives', { params: { limit: 200 } });
      const initiatives: Array<Record<string, unknown>> = listRes.data?.data ?? [];
      const byId = new Map(initiatives.map((i) => [String(i.id), i]));

      const grouped = new Map<string, TrackedMeasure>();
      for (const row of tracking) {
        const measure = byId.get(row.ballot_initiative_id);
        if (!measure) continue;
        const existing = grouped.get(row.ballot_initiative_id);
        if (existing) {
          existing.workspaceNames.push(row.workspace_name);
          existing.workspaceIds.push(row.workspace_id);
        } else {
          grouped.set(row.ballot_initiative_id, {
            id: String(measure.id),
            ballot_title: String(measure.ballot_title ?? 'Untitled measure'),
            popular_name: (measure.popular_name as string) || null,
            unofficial_caption: (measure.unofficial_caption as string) || null,
            measure_number: (measure.measure_number as string) ?? null,
            measure_type: (measure.measure_type as string) ?? null,
            status: (measure.status as string) ?? null,
            election_year: (measure.election_year as number) ?? null,
            workspaceNames: [row.workspace_name],
            workspaceIds: [row.workspace_id],
          });
        }
      }
      return Array.from(grouped.values());
    },
    { staleTime: 5 * 60 * 1000, enabled },
  );
}
